import { wgsl } from './tslCompat';

export const CONTACT_RECORD_META_OFFSET = 0;
export const CONTACT_RECORD_NORMAL_PEN_OFFSET = 1;
export const CONTACT_RECORD_ARM_A_OFFSET = 2;
export const CONTACT_RECORD_ARM_B_OFFSET = 3;
export const CONTACT_RECORD_CONSTRAINT_C0_OFFSET = 4;
export const CONTACT_RECORD_SHADOW_OFFSET = 5;
export const CONTACT_RECORD_DUAL_OFFSET = 6;
export const CONTACT_RECORD_PENALTY_OFFSET = 7;
export const CONTACT_RECORD_CACHE_OFFSET = 8;
export const CONTACT_RECORD_VEC4S = 9;
export const CONTACT_RECORD_FLOATS = CONTACT_RECORD_VEC4S * 4;

// Contact-info high-bit layout:
//   24..26 = AvbdStateStage's temporary world-arm scratch lifecycle
//   27..29 = persistent logical manifold slot (0..7)
//   30     = reserved and required to remain zero
//   31     = persistent swept/speculative marker
// Logical slot identity must not be reconstructed from a physical pool index:
// the compact active-contact pool is free to relocate a row while preserving
// its manifold identity. Keep this layout centralized so generation, prepare,
// capture, finalize and CPU diagnostics cannot silently disagree.
export const CONTACT_INFO_SPECULATIVE_BIT = 0x80000000;
export const CONTACT_INFO_SPECULATIVE_FEATURE_BASE = 0x1e0;
export const CONTACT_INFO_LOGICAL_SLOT_SHIFT = 27;
export const CONTACT_INFO_LOGICAL_SLOT_BITS = 3;
export const CONTACT_INFO_LOGICAL_SLOT_MASK = 0x38000000;
export const CONTACT_INFO_RESERVED_BIT_30 = 0x40000000;
export const CONTACT_INFO_PERSISTENT_MASK = 0xb8000000;

export const contactRecordHelpers = wgsl(/* wgsl */`
      const CONTACT_RECORD_META_OFFSET: u32 = ${CONTACT_RECORD_META_OFFSET}u;
      const CONTACT_RECORD_NORMAL_PEN_OFFSET: u32 = ${CONTACT_RECORD_NORMAL_PEN_OFFSET}u;
      const CONTACT_RECORD_ARM_A_OFFSET: u32 = ${CONTACT_RECORD_ARM_A_OFFSET}u;
      const CONTACT_RECORD_ARM_B_OFFSET: u32 = ${CONTACT_RECORD_ARM_B_OFFSET}u;
      const CONTACT_RECORD_CONSTRAINT_C0_OFFSET: u32 = ${CONTACT_RECORD_CONSTRAINT_C0_OFFSET}u;
      const CONTACT_RECORD_SHADOW_OFFSET: u32 = ${CONTACT_RECORD_SHADOW_OFFSET}u;
      const CONTACT_RECORD_DUAL_OFFSET: u32 = ${CONTACT_RECORD_DUAL_OFFSET}u;
      const CONTACT_RECORD_PENALTY_OFFSET: u32 = ${CONTACT_RECORD_PENALTY_OFFSET}u;
      const CONTACT_RECORD_CACHE_OFFSET: u32 = ${CONTACT_RECORD_CACHE_OFFSET}u;
      const CONTACT_RECORD_VEC4S: u32 = ${CONTACT_RECORD_VEC4S}u;
      const CONTACT_INFO_SPECULATIVE_BIT: u32 = ${CONTACT_INFO_SPECULATIVE_BIT}u;
      const CONTACT_INFO_SPECULATIVE_FEATURE_BASE: u32 = ${CONTACT_INFO_SPECULATIVE_FEATURE_BASE}u;
      const CONTACT_INFO_LOGICAL_SLOT_SHIFT: u32 = ${CONTACT_INFO_LOGICAL_SLOT_SHIFT}u;
      const CONTACT_INFO_LOGICAL_SLOT_MASK: u32 = ${CONTACT_INFO_LOGICAL_SLOT_MASK}u;
      const CONTACT_INFO_RESERVED_BIT_30: u32 = ${CONTACT_INFO_RESERVED_BIT_30}u;
      const CONTACT_INFO_PERSISTENT_MASK: u32 = ${CONTACT_INFO_PERSISTENT_MASK}u;

      fn contactInfoIsSpeculative(encodedInfo: u32) -> bool {
        return (encodedInfo & CONTACT_INFO_SPECULATIVE_BIT) != 0u;
      }

      fn contactInfoLogicalSlot(encodedInfo: u32) -> u32 {
        return (encodedInfo & CONTACT_INFO_LOGICAL_SLOT_MASK)
          >> CONTACT_INFO_LOGICAL_SLOT_SHIFT;
      }

      fn contactInfoWithLogicalSlot(encodedInfo: u32, logicalSlot: u32) -> u32 {
        return (encodedInfo & ~CONTACT_INFO_LOGICAL_SLOT_MASK)
          | ((logicalSlot & 0x7u) << CONTACT_INFO_LOGICAL_SLOT_SHIFT);
      }

      fn contactInfoPersistentBits(encodedInfo: u32) -> u32 {
        return encodedInfo & CONTACT_INFO_PERSISTENT_MASK;
      }

      fn contactConstraintC0Scale(encodedInfo: u32, regularizationAlpha: f32) -> f32 {
        return select(
          1.0 - regularizationAlpha,
          1.0,
          contactInfoIsSpeculative(encodedInfo),
        );
      }

      fn contactRecordBase(p: u32) -> u32 {
        return p * CONTACT_RECORD_VEC4S;
      }

      fn loadContactMeta(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_META_OFFSET];
      }

      fn storeContactMeta(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_META_OFFSET] = value;
      }

      fn loadContactNormalPen(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_NORMAL_PEN_OFFSET];
      }

      fn storeContactNormalPen(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_NORMAL_PEN_OFFSET] = value;
      }

      fn loadContactArmA(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_ARM_A_OFFSET];
      }

      fn storeContactArmA(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_ARM_A_OFFSET] = value;
      }

      fn loadContactArmB(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_ARM_B_OFFSET];
      }

      fn storeContactArmB(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_ARM_B_OFFSET] = value;
      }

      fn loadContactConstraintC0(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_CONSTRAINT_C0_OFFSET];
      }

      fn storeContactConstraintC0(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_CONSTRAINT_C0_OFFSET] = value;
      }

      fn loadContactShadow(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_SHADOW_OFFSET];
      }

      fn storeContactShadow(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_SHADOW_OFFSET] = value;
      }

      fn loadContactDual(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_DUAL_OFFSET];
      }

      fn storeContactDual(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_DUAL_OFFSET] = value;
      }

      fn loadContactPenalty(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4f {
        return pairContacts[contactRecordBase(p) + CONTACT_RECORD_PENALTY_OFFSET];
      }

      fn storeContactPenalty(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4f,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_PENALTY_OFFSET] = value;
      }

      fn loadContactCache(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> vec4u {
        return bitcast<vec4u>(pairContacts[contactRecordBase(p) + CONTACT_RECORD_CACHE_OFFSET]);
      }

      fn storeContactCache(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: vec4u,
      ) {
        pairContacts[contactRecordBase(p) + CONTACT_RECORD_CACHE_OFFSET] = bitcast<vec4f>(value);
      }

      fn loadContactCacheWord(pairContacts: ptr<storage, array<vec4f>, read_write>, p: u32) -> u32 {
        return loadContactCache(pairContacts, p).x;
      }

      fn storeContactCacheWord(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        value: u32,
      ) {
        let cache = loadContactCache(pairContacts, p);
        storeContactCache(pairContacts, p, vec4u(value, cache.y, cache.z, cache.w));
      }

      fn loadContactTangentT1(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
      ) -> vec3f {
        return bitcast<vec3f>(loadContactCache(pairContacts, p).yzw);
      }

      fn storeContactTangentT1(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        tangentT1: vec3f,
      ) {
        let cache = loadContactCache(pairContacts, p);
        let tangentBits = bitcast<vec3u>(tangentT1);
        storeContactCache(pairContacts, p, vec4u(cache.x, tangentBits));
      }
`);

export function contactRecordBaseFloatIndex(contactIndex: number): number {
  return contactIndex * CONTACT_RECORD_FLOATS;
}

export function decodeContactLogicalSlot(encodedInfo: number): number {
  return (encodedInfo >>> CONTACT_INFO_LOGICAL_SLOT_SHIFT) & 0x7;
}

export function encodeContactLogicalSlot(encodedInfo: number, logicalSlot: number): number {
  return (
    (encodedInfo & ~CONTACT_INFO_LOGICAL_SLOT_MASK)
    | ((Math.trunc(logicalSlot) & 0x7) << CONTACT_INFO_LOGICAL_SLOT_SHIFT)
  ) >>> 0;
}

export function contactInfoPersistentBits(encodedInfo: number): number {
  return (encodedInfo & CONTACT_INFO_PERSISTENT_MASK) >>> 0;
}

export function contactRecordVec4FloatIndex(contactIndex: number, vec4Offset: number): number {
  return contactRecordBaseFloatIndex(contactIndex) + vec4Offset * 4;
}

export function contactRecordVec4Slice(
  pairContacts: Float32Array,
  contactIndex: number,
  vec4Offset: number,
): Float32Array {
  const base = contactRecordVec4FloatIndex(contactIndex, vec4Offset);
  return pairContacts.subarray(base, base + 4);
}
